import { balance, type Ecriture } from './compta'
import type { Cloture, EvenementCloture, LigneBalanceArchivee } from './types'

/**
 * Clôture d'exercice — logique pure, transposée de la console HTML
 * (`closureState`, `activeClosures`, `lockDate`, `closureSnapshot`,
 * `closHash`), avec deux corrections.
 *
 * Persistance : `accounting_closures` et `accounting_closure_events`
 * (migration 0008), EN AJOUT SEUL — aucune policy UPDATE ni DELETE. Une
 * réouverture n'efface pas l'archive : elle ajoute un événement, et l'état
 * courant d'un exercice se lit au DERNIER événement de l'année.
 */

/** `AAAA-MM-JJ` d'une date ISO (ou chaîne vide si illisible). */
export function jourIso(d: string): string {
  const t = new Date(d)
  return Number.isNaN(t.getTime()) ? '' : t.toISOString().slice(0, 10)
}

/**
 * État courant de chaque exercice : dernière action dans l'ordre CHRONOLOGIQUE
 * SERVEUR (`at`, horodaté par la base) — jamais l'horloge du poste.
 */
export function etatExercices(ev: EvenementCloture[]): Map<number, 'cloture' | 'reouverture'> {
  const st = new Map<number, 'cloture' | 'reouverture'>()
  for (const e of [...ev].sort((a, b) => a.le.localeCompare(b.le))) st.set(e.annee, e.action)
  return st
}

/** Les archives en vigueur : la plus récente de chaque année, si l'année n'a pas été rouverte depuis. */
export function cloturesActives(clotures: Cloture[], ev: EvenementCloture[]): Cloture[] {
  const st = etatExercices(ev)
  const parAnnee = new Map<number, Cloture>()
  for (const c of [...clotures].sort((a, b) => a.clotureLe.localeCompare(b.clotureLe))) parAnnee.set(c.annee, c)
  return [...parAnnee.values()].filter((c) => st.get(c.annee) !== 'reouverture')
}

/** Date jusqu'à laquelle (incluse) les écritures sont verrouillées, ou `null`. */
export function dateVerrou(clotures: Cloture[], ev: EvenementCloture[]): string | null {
  const a = cloturesActives(clotures, ev)
    .map((c) => c.dateVerrou)
    .sort()
  return a.length ? (a[a.length - 1] ?? null) : null
}

export function estVerrouille(date: string, verrou: string | null): boolean {
  return verrou ? jourIso(date) <= verrou : false
}

export function ecrituresJusquA(E: Ecriture[], fin: string): Ecriture[] {
  return E.filter((e) => {
    const j = jourIso(e.date)
    return j !== '' && j <= fin
  })
}

/** Résultat cumulé à une date, en soldes nets. */
export function resultatAu(E: Ecriture[], fin: string): { produits: number; charges: number; resultat: number } {
  let produits = 0
  let charges = 0
  for (const e of ecrituresJusquA(E, fin)) {
    if (e.compte.startsWith('7')) produits += e.credit - e.debit
    if (e.compte.startsWith('6')) charges += e.debit - e.credit
  }
  return { produits, charges, resultat: produits - charges }
}

export function annees(E: Ecriture[]): number[] {
  const s = new Set<number>()
  for (const e of E) {
    const y = new Date(e.date).getFullYear()
    if (Number.isFinite(y)) s.add(y)
  }
  return [...s].sort((a, b) => a - b)
}

/** Premier exercice non clôturé ayant des écritures, sinon l'année en cours. */
export function exerciceOuvert(E: Ecriture[], actives: Cloture[], maintenant: Date = new Date()): number {
  const fermees = new Set(actives.map((c) => c.annee))
  return annees(E).find((y) => !fermees.has(y)) ?? maintenant.getFullYear()
}

export interface Instantane {
  annee: number
  fin: string
  balance: LigneBalanceArchivee[]
  produits: number
  charges: number
  resultat: number
  totalDebit: number
  totalCredit: number
}

export function instantane(E: Ecriture[], annee: number, plan: Record<string, string> = {}): Instantane {
  const fin = `${annee}-12-31`
  const jusqua = ecrituresJusquA(E, fin)
  const bal: LigneBalanceArchivee[] = balance(jusqua, plan).map((r) => ({
    code: r.code,
    label: r.libelle,
    debit: r.debit,
    credit: r.credit,
    solde: r.solde,
  }))
  const r = resultatAu(E, fin)
  return {
    annee,
    fin,
    balance: bal,
    ...r,
    totalDebit: bal.reduce((s, x) => s + x.debit, 0),
    totalCredit: bal.reduce((s, x) => s + x.credit, 0),
  }
}

/* ---------------------------------------------------------------------- */
/*  Empreinte d'intégrité                                                  */
/* ---------------------------------------------------------------------- */

/** Le hachage FNV-1a 32 bits de la console HTML, à l'identique. */
export function hacher(s: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0
  }
  return `0000000${h.toString(16)}`.slice(-8).toUpperCase()
}

/**
 * Empreinte d'une archive.
 *
 * ⚠️ CORRECTION par rapport à l'original. Le HTML hachait
 * `JSON.stringify({year,end,res,bal})` sur des objets dans l'ordre de leur
 * création, puis vérifiait l'archive RELUE DE LA BASE. Or `balance` est une
 * colonne jsonb, et jsonb RÉORDONNE les clés (par longueur puis
 * alphabétiquement) : relue, chaque ligne devient
 * `{code,debit,label,solde,credit}` — l'empreinte ne correspondait plus et
 * l'écran criait « archive altérée » sur une archive intacte.
 *
 * On reconstruit donc chaque objet dans un ordre de clés EXPLICITE avant de
 * hacher. Le résultat est identique à ce que le HTML calculait au moment de
 * clôturer : ses archives se vérifient aussi.
 *
 * Ce n'est PAS une preuve cryptographique (32 bits) : elle détecte une
 * altération accidentelle. L'immutabilité réelle vient de la base (ajout seul).
 */
export function empreinte(annee: number, fin: string, resultat: number, bal: LigneBalanceArchivee[]): string {
  const canonique = {
    year: annee,
    end: fin,
    res: resultat,
    bal: bal.map((r) => ({ code: r.code, label: r.label, debit: r.debit, credit: r.credit, solde: r.solde })),
  }
  return hacher(JSON.stringify(canonique))
}

export function archiveIntegre(c: Cloture): boolean {
  if (!c.empreinte) return false
  return empreinte(c.annee, c.dateVerrou, c.resultat, c.balance) === c.empreinte
}
